<script setup>
import JobAvatar from 'components/JobAvatar.vue';
import { computed } from 'vue';

const $props = defineProps({
    job: {
        type: Object,
        required: true
    },
    avatarSize: {
        type: String
    },
    jobTitleElement: {
        type: String,
        default: 'h4'
    },
    jobTitleClass: {
        type: String
    },
    companyNameElement: {
        type: String,
        default: 'p'
    },
    companyNameClass: {
        type: String
    },
    dense: {
        type: Boolean,
        default: false
    },
    minimal: {
        type: Boolean,
        default: false
    }
});

const avatarSize = computed(() => {
    return $props.avatarSize || ($props.dense ? '60px' : '90px');
});

const jobTitleClass = computed(() => {
    const fallbackClasses = [
        'q-mt-none',
        'q-mb-xs',
        $props.dense
            ? 'text-h6'
            : 'text-h5'
    ];
    return [
        { ['adaptive-ellipsis']: $props.minimal },
        $props.jobTitleClass || fallbackClasses
    ].flat();
});

const companyNameClass = computed(() => {
    const fallbackClasses = [
        'q-mt-none',
        'q-mb-none',
        'text-font-heading',
        'text-weight-regular',
        { 'text-subtitle1': !$props.dense }
    ];
    return [
        { ['adaptive-ellipsis']: $props.minimal },
        $props.companyNameClass || fallbackClasses
    ].flat();
});
</script>

<template>
    <div class="job-header-info row q-col-gutter-x-md flex items-center no-wrap">
        <div class="overflow-hidden">
            <template v-if="!minimal && job">
                <JobAvatar
                    v-if="job.logoUrl"
                    :class="['rounded-borders', dense ? 'q-mb-md' : 'q-mb-lg']"
                    :job
                    :size="avatarSize"
                    hide-fallback
                />
            </template>
            <template v-else-if="!minimal">
                <q-skeleton :size="avatarSize" class="rounded-borders" square type="QAvatar"/>
            </template>
            <template v-if="job">
                <component :is="jobTitleElement" :class="['job-title', jobTitleClass]" :title="job.title">
                    {{ job.title }}
                </component>
                <component :is="companyNameElement" :class="['company-name', companyNameClass]" :title="job.company.name">
                    {{ job.company.name }}
                </component>
            </template>
            <template v-else>
                <q-skeleton height="32px" type="text" width="400px"/>
                <q-skeleton height="24px" type="text" width="220px"/>
            </template>
        </div>
        <div v-if="$slots.append" class="flex col justify-end">
            <slot name="append"></slot>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.job-header-info {
    min-width: 0;
    width: 100%;
}

.adaptive-ellipsis {
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;

    @media (max-width: $breakpoint-xs-max) {
        -webkit-line-clamp: 3;
    }

    @media (min-width: $breakpoint-sm-min) and (max-width: $breakpoint-sm-max) {
        -webkit-line-clamp: 2;
    }

    @media (min-width: $breakpoint-md-min) {
        -webkit-line-clamp: 1;
    }
}
</style>
